import { useEffect } from "react"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { GeneralCoachChat } from "@/components/coach-chat-general"
import { useCoachThread } from "@/lib/queries"
import { productMedia } from "@/lib/product-media"
import { useCoach } from "@/lib/use-coach"
import { useSessionContext } from "@/lib/session"
import { MEDIATHEK_ENABLED } from "@/lib/feature-flags"
import { parseChatSearch, withoutQuestion } from "@/lib/chat-navigation"
import { hasOwnEntryPoint, threadDestination } from "@/lib/recent-entries"
import { COACH_START_PROMPTS } from "@/lib/coach-prompts"
import { COACH_FALLBACK_LABEL } from "@nl/shared"

function CoachChatPage() {
  const { role } = useSessionContext()
  const coach = useCoach()
  const name = coach?.name ?? COACH_FALLBACK_LABEL
  const navigate = useNavigate()
  const { frage, gespraech } = Route.useSearch()
  const { data: storedThread } = useCoachThread(gespraech)
  const product = productMedia.find(
    (product) => product.key === storedThread?.produkt
  )
  // Old links and generic history entries: a thread that was started under its
  // own page reopens there, because the API refuses to continue it as a chat.
  const ownEntryPoint = Boolean(storedThread && hasOwnEntryPoint(storedThread.mode))
  useEffect(() => {
    if (ownEntryPoint && storedThread && gespraech)
      void navigate({ ...threadDestination(storedThread), replace: true })
  }, [ownEntryPoint, storedThread, gespraech, navigate])
  useEffect(() => {
    if (product && gespraech)
      void navigate({
        to: "/produkttraining",
        search: { produkt: product.key, gespraech },
        replace: true,
      })
  }, [product, gespraech, navigate])
  useEffect(() => {
    if (role === "coach") void navigate({ to: "/coach", replace: true })
    if (role === "manager") void navigate({ to: "/management", replace: true })
  }, [role, navigate])
  if ((product && gespraech) || ownEntryPoint) return null
  return (
    <GeneralCoachChat
      threadId={gespraech}
      initialPrompt={frage}
      onInitialPromptTaken={() =>
        void navigate({ to: "/chat", search: withoutQuestion, replace: true })
      }
      mode="open"
      barSize="md"
      autoFocus
      onThreadCreated={(id) =>
        void navigate({ to: "/chat", search: { gespraech: id }, replace: true })
      }
      onReset={() => void navigate({ to: "/chat", search: {}, replace: true })}
      empty={{
        title: `Was steht an?`,
        hint: `Besprich mit ${name} deine Produktfragen oder eine Beratung, die du erlebt hast.`,
        prompts: COACH_START_PROMPTS,
      }}
      placeholder={`Schreib ${name}, was ansteht …`}
    />
  )
}
export const Route = createFileRoute("/_app/chat")({
  validateSearch: parseChatSearch,
  beforeLoad: ({ search }) => {
    if (!MEDIATHEK_ENABLED) throw redirect({ to: "/", search, replace: true })
  },
  component: CoachChatPage,
})
